import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Save, Radio, ExternalLink, Upload, RefreshCcw, User as UserIcon, Handshake } from "lucide-react";
import HostPayouts from "@/components/HostPayouts";
import HostDonations from "@/components/HostDonations";
import HostOnboarding from "@/components/host/HostOnboarding";
import HostContracts from "@/components/contracts/HostContracts";
import MeetingsPanel from "@/components/meetings/MeetingsPanel";
import MeetingAlert from "@/components/meetings/MeetingAlert";
import { useMeetings } from "@/components/meetings/useMeetings";

const DAY_SHORT = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

function HostShowItem({ s, collab = false }) {
  const days = (s.days_of_week && s.days_of_week.length ? s.days_of_week : [s.day_of_week ?? 0]);
  return (
    <li className={`border-l-2 pl-3 ${collab ? "border-[#5AC8FA]" : "border-[var(--ff-red)]"}`} data-testid={`host-show-${s.id}`}>
      <div className="font-display text-lg leading-tight">{s.title}</div>
      <div className="font-mono-ui text-[0.6rem] text-[var(--ff-text-dim)] uppercase tracking-[0.12em] mt-1">
        {s.start_time ? `${days.map((d) => DAY_SHORT[d]).join(" · ")} / ${s.start_time}–${s.end_time}` : "Unscheduled / TBA"}
      </div>
      {s.youtube_url && (
        <a href={s.youtube_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[0.6rem] text-[var(--ff-red)] font-mono-ui uppercase tracking-[0.12em] mt-1 hover:underline">
          <ExternalLink size={9} /> Live link
        </a>
      )}
    </li>
  );
}

export default function HostStudio() {
  const { user, loading: authLoading, refresh, logout } = useAuth();
  const [host, setHost] = useState(null);
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [notLinked, setNotLinked] = useState(false);
  const [studioTab, setStudioTab] = useState(() => (new URLSearchParams(window.location.search).get("tab") === "meetings" ? "meetings" : "bio"));
  const [onboarding, setOnboarding] = useState(null);
  const meetingsOk = !!user && !user.is_banned && (user.roles?.includes("host") || user.role === "admin");
  const meetings = useMeetings(meetingsOk && !!onboarding && (!onboarding.required || onboarding.complete));

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/host/me");
      if (data) {
        setHost(data);
        setShows(data.shows || []);
        setNotLinked(false);
      } else {
        setNotLinked(true);
      }
    } catch (e) {
      if (e.response?.data?.detail === "host_onboarding_required") {
        checkOnboarding();
      } else if (e.response?.status === 404) {
        setNotLinked(true);
      } else {
        toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
      }
    } finally {
      setLoading(false);
    }
  };

  const checkOnboarding = async () => {
    try {
      const { data } = await api.get("/host/onboarding");
      setOnboarding(data);
      if (!data.required || data.complete) load();
    } catch { setOnboarding({ required: false, complete: true }); load(); }
  };

  useEffect(() => { if (user) checkOnboarding(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  if (authLoading) return <div className="p-12 text-[var(--ff-text-dim)] font-mono-ui text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.is_banned) return <Navigate to="/banned" replace />;
  const hasHostRole = user.roles && user.roles.includes("host");
  const isAdmin = user.role === "admin";
  if (!hasHostRole && !isAdmin) return <Navigate to="/watchlist" replace />;
  if (!onboarding) return <div className="p-12 text-[var(--ff-text-dim)] font-mono-ui text-sm">Loading…</div>;
  if (onboarding.required && !onboarding.complete) {
    return <HostOnboarding state={onboarding} onComplete={checkOnboarding} />;
  }

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/host/me", {
        title: host.title || "",
        bio: host.bio || "",
        avatar_url: host.avatar_url || "",
        social_url: host.social_url || "",
      });
      setHost({ ...host, ...data });
      toast.success("Bio saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const refreshRoles = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/user/refresh-roles");
      await refresh();
      toast.success(`Roles refreshed: ${data.roles.join(", ")}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setRefreshing(false);    }
  };

  const uploadAvatar = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload-host-avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setHost({ ...host, avatar_url: data.url });
      toast.success("Avatar uploaded — remember to Save");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const savePaypalEmail = async () => {
    const email = (host?.paypal_email || "").trim();
    if (!email) { toast.error("Enter your PayPal email first."); return; }
    try {
      await api.put("/host/paypal-email", { paypal_email: email });
      toast.success("PayPal payout email saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24" data-testid="host-studio-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10 pb-8 border-b border-[var(--ff-border)]">
        <div>
          <div className="label-eyebrow mb-1 text-[var(--ff-red)]">▮ HOST STUDIO</div>
          <h1 className="font-display text-4xl lg:text-5xl leading-none" data-testid="host-studio-heading">MY BIO.</h1>
          <p className="text-[var(--ff-text-dim)] text-sm mt-2 max-w-xl">
            You&apos;re seeing this because you carry the Host role in the Freshy Fresh Discord. Update your public bio, headshot and social link. Your host name is managed by admins.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <button onClick={refreshRoles} disabled={refreshing} className="btn-ghost text-xs" data-testid="refresh-roles-btn">
            <RefreshCcw size={12} className={refreshing ? "animate-spin" : ""} /> Sync Discord Roles
          </button>
          <button onClick={async () => { await logout(); window.location.href = "/"; }} className="btn-ghost text-xs" data-testid="host-logout-btn">Sign Out</button>
        </div>
      </div>

      <MeetingAlert {...meetings} onOpen={() => setStudioTab("meetings")} />

      {!loading && (host || notLinked) && (
        <div className="flex flex-wrap gap-px bg-[var(--ff-border)] mb-8 w-fit" data-testid="studio-tabs">
          {[{ id: "bio", label: "Bio & Payouts" }, { id: "donations", label: "Donation Alerts" }, { id: "contracts", label: "Contracts" }, { id: "meetings", label: "Meetings", badge: meetings.data?.pending_count }].map((t) => (
            <button
              key={t.id}
              onClick={() => setStudioTab(t.id)}
              className={`px-5 py-2.5 font-mono-ui text-xs uppercase tracking-[0.15em] transition-colors ${studioTab === t.id ? "bg-[var(--ff-red)] text-white" : "bg-[var(--ff-bg)] text-[var(--ff-text-dim)] hover:text-white"}`}
              data-testid={`studio-tab-${t.id}`}
            >
              {t.label}
              {t.badge > 0 && <span className="ml-2 px-1.5 py-0.5 bg-white text-black text-[0.6rem] font-bold" data-testid="studio-tab-meetings-badge">{t.badge}</span>}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="text-[var(--ff-text-dim)] font-mono-ui text-sm">Loading your host profile…</div>
      ) : studioTab === "meetings" ? (
        <MeetingsPanel {...meetings} />
      ) : notLinked ? (
        <div className="surface-card p-12 text-center" data-testid="host-not-linked">
          <UserIcon size={32} className="mx-auto text-[var(--ff-text-dim)] mb-4" />
          <div className="font-display text-2xl mb-2">NO HOST CARD LINKED.</div>
          <p className="text-[var(--ff-text-dim)] text-sm mb-6 max-w-md mx-auto">
            An admin hasn&apos;t linked your Discord account to a host record yet. Ask an admin to add a host card matching your Discord name, then hit Sync Discord Roles.
          </p>
          <button onClick={refreshRoles} disabled={refreshing} className="btn-primary" data-testid="host-not-linked-refresh">
            <RefreshCcw size={12} className={refreshing ? "animate-spin" : ""} /> Retry
          </button>
        </div>
      ) : host && studioTab === "donations" ? (
        <HostDonations />
      ) : host && studioTab === "contracts" ? (
        <HostContracts />
      ) : host && (
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Editor */}
          <div className="lg:col-span-2 surface-card p-8 space-y-6" data-testid="host-editor">
            <div>
              <label className="label-eyebrow block mb-2">Host Name (admin-managed)</label>
              <div className="font-display text-3xl" data-testid="host-name-readonly">{host.name}</div>
              <div className="font-mono-ui text-[0.65rem] text-[var(--ff-text-dim)] mt-1 uppercase tracking-[0.15em]">
                To change your public name, contact an admin.
              </div>
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Title / Tagline</label>
              <input
                type="text"
                value={host.title || ""}
                maxLength={80}
                onChange={(e) => setHost({ ...host, title: e.target.value })}
                placeholder="e.g. Lead Anchor · Prime Time News"
                className="w-full px-4 py-3 text-sm"
                data-testid="host-title-input"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Bio</label>
              <textarea
                value={host.bio || ""}
                maxLength={1200}
                onChange={(e) => setHost({ ...host, bio: e.target.value })}
                rows={7}
                placeholder="Tell viewers who you are, what you cover, and why they should tune in."
                className="w-full px-4 py-3 text-sm font-mono-ui leading-relaxed"
                data-testid="host-bio-input"
              />
              <div className="font-mono-ui text-[0.6rem] text-[var(--ff-text-dim)] mt-1 uppercase tracking-[0.15em] text-right">
                {(host.bio || "").length} / 1200
              </div>
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Social URL</label>
              <input
                type="url"
                value={host.social_url || ""}
                onChange={(e) => setHost({ ...host, social_url: e.target.value })}
                placeholder="https://x.com/yourhandle"
                className="w-full px-4 py-3 text-sm"
                data-testid="host-social-input"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">
                PayPal Payout Email <span className="text-[0.6rem] font-mono-ui text-[var(--ff-text-dim)] normal-case tracking-normal">— where PayPal sends your host cut</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={host.paypal_email || ""}
                  onChange={(e) => setHost({ ...host, paypal_email: e.target.value })}
                  placeholder="you@paypal.com"
                  className="flex-1 px-4 py-3 text-sm font-mono-ui"
                  data-testid="host-paypal-email-input"
                />
                <button
                  onClick={savePaypalEmail}
                  className="btn-ghost text-xs whitespace-nowrap"
                  data-testid="host-paypal-email-save"
                  type="button"
                >
                  Save Email
                </button>
              </div>
              <div className="font-mono-ui text-[0.6rem] text-[var(--ff-text-dim)] uppercase tracking-[0.15em] mt-1">
                Optional — required only if the site is set to PayPal or Both. Stripe payouts use Stripe Connect below.
              </div>
            </div>
            <div className="pt-4 border-t border-[var(--ff-border)] flex items-center justify-between">
              <div className="font-mono-ui text-[0.65rem] text-[var(--ff-text-dim)] uppercase tracking-[0.15em]">
                Changes go live on your public host page immediately.
              </div>
              <button onClick={save} disabled={saving} className="btn-primary" data-testid="host-save-btn">
                <Save size={14} /> {saving ? "Saving…" : "Save Bio"}
              </button>
            </div>
          </div>

          {/* Sidebar: avatar + shows */}
          <div className="space-y-6">
            <div className="surface-card p-6" data-testid="host-avatar-panel">
              <div className="label-eyebrow mb-3">▮ HEADSHOT</div>
              <div className="flex flex-col items-center gap-4">
                {host.avatar_url ? (
                  <img src={host.avatar_url} alt={host.name} className="w-40 h-40 object-cover ring-2 ring-[var(--ff-border)]" data-testid="host-avatar-preview" />
                ) : (
                  <div className="w-40 h-40 flex items-center justify-center bg-[var(--ff-red)] text-white font-display text-6xl">
                    {host.name?.[0] || "?"}
                  </div>
                )}
                <label className="btn-ghost cursor-pointer inline-flex items-center gap-2 text-xs">
                  <Upload size={12} /> {uploading ? "Uploading…" : "Change Photo"}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => uploadAvatar(e.target.files?.[0])}
                    data-testid="host-avatar-file-input"
                  />
                </label>
                <input
                  type="url"
                  value={host.avatar_url || ""}
                  onChange={(e) => setHost({ ...host, avatar_url: e.target.value })}
                  placeholder="or paste an image URL"
                  className="w-full px-3 py-2 text-xs"
                  data-testid="host-avatar-url-input"
                />
              </div>
            </div>

            <div className="surface-card p-6" data-testid="host-shows-panel">
              <div className="label-eyebrow mb-3 flex items-center gap-2"><Radio size={12} /> ▮ MY SHOWS · {shows.filter((s) => !s.is_collab).length}</div>
              {shows.length === 0 ? (
                <p className="text-xs text-[var(--ff-text-dim)]">No shows assigned yet. Ask an admin to add you to a show.</p>
              ) : (
                <>
                  <ul className="space-y-3" data-testid="host-own-shows">
                    {shows.filter((s) => !s.is_collab).map((s) => <HostShowItem key={s.id} s={s} />)}
                  </ul>
                  {!shows.some((s) => !s.is_collab) && (
                    <p className="text-xs text-[var(--ff-text-dim)]" data-testid="host-own-shows-empty">No Freshy Fresh shows yet — only collaborations.</p>
                  )}
                  {shows.some((s) => s.is_collab) && (
                    <div className="mt-6 pt-4 border-t border-[#5AC8FA]/30" data-testid="host-collab-shows">
                      <div className="label-eyebrow mb-3 text-[#5AC8FA] flex items-center gap-2"><Handshake size={12} /> ▮ COLLABORATIONS · {shows.filter((s) => s.is_collab).length}</div>
                      <ul className="space-y-3">
                        {shows.filter((s) => s.is_collab).map((s) => <HostShowItem key={s.id} s={s} collab />)}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {host && studioTab === "bio" && <HostPayouts />}
    </div>
  );
}
